/**
 * Domain Card Loader Utility
 *
 * Provides functions to load and filter domain cards from JSON data
 */

import { DomainCard, DomainCardsData } from '../types/domain-card';
import { findClassById } from './classDataLoader';
import domainCardsData from '../data/domain-cards.json';

// Cache for loaded domain cards
let domainCardsCache: DomainCard[] | null = null;

/**
 * Load all domain cards from JSON file
 */
async function loadAllDomainCards(): Promise<DomainCard[]> {
  if (domainCardsCache) {
    return domainCardsCache;
  }

  try {
    const data: DomainCardsData = domainCardsData as DomainCardsData;
    domainCardsCache = data.domainCards || [];
    return domainCardsCache;
  } catch (error) {
    console.error('Error loading domain cards:', error);
    throw error;
  }
}

/**
 * Load domain cards available for a specific class
 * Returns cards from the class's available domains, sorted by domain and level
 */
export async function loadClassDomainCards(className: string): Promise<DomainCard[]> {
  try {
    // Load class data to get available domains
    const classData = await findClassById(className);
    if (!classData || !classData.domains) {
      console.error(`Class ${className} not found or has no domains`);
      return [];
    }

    const classDomains = classData.domains.map(d => d.toLowerCase());

    // Load all domain cards
    const allCards = await loadAllDomainCards();

    // Filter cards from class domains
    const classCards = allCards.filter(card =>
      classDomains.includes(card.domain.toLowerCase())
    );

    // Sort by domain and then by level
    return classCards.sort((a, b) => {
      if (a.domain !== b.domain) {
        return a.domain.localeCompare(b.domain);
      }
      return a.level - b.level;
    });
  } catch (error) {
    console.error('Error loading class domain cards:', error);
    throw error;
  }
}

/**
 * Filter cards by specific domain
 */
export function filterCardsByDomain(cards: DomainCard[], domain: string): DomainCard[] {
  const normalizedDomain = domain.toLowerCase();
  return cards.filter(card => card.domain.toLowerCase() === normalizedDomain);
}

/**
 * Group cards by domain and level
 * Returns a nested Map: domain -> level -> cards[]
 */
export function groupCardsByDomainAndLevel(
  cards: DomainCard[]
): Map<string, Map<number, DomainCard[]>> {
  const grouped = new Map<string, Map<number, DomainCard[]>>();

  cards.forEach(card => {
    const domain = card.domain;
    if (!grouped.has(domain)) {
      grouped.set(domain, new Map<number, DomainCard[]>());
    }

    const domainMap = grouped.get(domain)!;
    const level = card.level;
    if (!domainMap.has(level)) {
      domainMap.set(level, []);
    }

    domainMap.get(level)!.push(card);
  });

  return grouped;
}

/**
 * Get a single card by ID
 */
export async function getCardById(id: string): Promise<DomainCard | null> {
  try {
    const allCards = await loadAllDomainCards();
    return allCards.find(card => card.id === id) || null;
  } catch (error) {
    console.error('Error getting card by ID:', error);
    return null;
  }
}

/**
 * Get multiple cards by IDs
 */
export async function getCardsByIds(ids: string[]): Promise<DomainCard[]> {
  try {
    const allCards = await loadAllDomainCards();
    return allCards.filter(card => ids.includes(card.id));
  } catch (error) {
    console.error('Error getting cards by IDs:', error);
    return [];
  }
}

/**
 * Filter cards by level (useful for character creation)
 */
export function filterCardsByLevel(cards: DomainCard[], maxLevel: number = 1): DomainCard[] {
  return cards.filter(card => card.level <= maxLevel);
}

/**
 * Clear the domain cards cache (useful for testing)
 */
export function clearDomainCardsCache(): void {
  domainCardsCache = null;
}
